import { useCallback, useMemo } from 'react';
import { createFileRoute, Link, Outlet, useRouter } from '@tanstack/react-router';
import { BarChart3, Plus } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { useHasPermission, useIsFieldHidden } from '@/hooks/usePermissions';
import {
  EstimateListPanel,
  type EstimateListFilters,
} from '@/components/organisms/estimates/EstimateListPanel';
import { EstimateTableView } from '@/components/organisms/estimates/EstimateTableView';
import { EntityIndexLayout, WorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import { STATUS_FILTERS } from '@/components/organisms/estimates/support/estimate-ui-helpers';
import { useEntityRouteState } from '@/hooks/navigation/useEntityRouteState';
import type { EstimateStatus } from '@/types/estimate';

const NUMBER_FILTER_OPS = ['eq', 'gt', 'lt', 'gte', 'lte'] as const;

export const Route = createFileRoute('/_dashboard/dashboard/estimates')({
  validateSearch: (search: Record<string, unknown>): EstimateListFilters => ({
    q: typeof search.q === 'string' ? search.q : '',
    status: STATUS_FILTERS.some((f) => f.value === search.status)
      ? (search.status as EstimateStatus | 'all')
      : 'all',
    estimateNumber: typeof search.estimateNumber === 'string' ? search.estimateNumber : '',
    customerName: typeof search.customerName === 'string' ? search.customerName : '',
    totalOp: NUMBER_FILTER_OPS.includes(search.totalOp as (typeof NUMBER_FILTER_OPS)[number])
      ? (search.totalOp as EstimateListFilters['totalOp'])
      : 'eq',
    totalValue: typeof search.totalValue === 'string' ? search.totalValue : '',
    updatedFrom: typeof search.updatedFrom === 'string' ? search.updatedFrom : undefined,
    updatedTo: typeof search.updatedTo === 'string' ? search.updatedTo : undefined,
    serviceAddress: typeof search.serviceAddress === 'string' ? search.serviceAddress : '',
    customerEmail: typeof search.customerEmail === 'string' ? search.customerEmail : '',
    customerPhone: typeof search.customerPhone === 'string' ? search.customerPhone : '',
    salespersonId: typeof search.salespersonId === 'string' ? search.salespersonId : undefined,
    businessUnitId: typeof search.businessUnitId === 'string' ? search.businessUnitId : undefined,
    page: typeof search.page === 'number' && search.page > 0 ? Math.floor(search.page) : 1,
    sort:
      typeof search.sort === 'number' && search.sort >= 0
        ? Math.min(Math.floor(search.sort), 6)
        : 0,
    dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
    dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
    tagId: typeof search.tagId === 'string' ? search.tagId : undefined,
  }),
  component: EstimatesLayout,
});

function cleanEstimateSearch(next: Record<string, unknown>) {
  const defaults: Partial<Record<keyof EstimateListFilters, unknown>> = {
    q: '',
    status: 'all',
    estimateNumber: '',
    customerName: '',
    totalOp: 'eq',
    totalValue: '',
    serviceAddress: '',
    customerEmail: '',
    customerPhone: '',
    page: 1,
    sort: 0,
  };

  (Object.keys(next) as Array<keyof EstimateListFilters>).forEach((key) => {
    if (next[key] == null || next[key] === defaults[key]) delete next[key];
  });

  return next as unknown as EstimateListFilters;
}

function EstimatesLayout() {
  const router = useRouter();
  const filters = Route.useSearch();
  const {
    selectedId,
    isFormMode,
    hasChildRoute,
    childSegments,
    preservedSearch: listSearch,
  } = useEntityRouteState({
    basePath: '/dashboard/estimates',
    search: filters,
    cleanSearch: cleanEstimateSearch,
    formSegments: ['payment-schedule'],
  });
  const isReportsRoute = childSegments[0] === 'reports';

  function handleSelect(id: string) {
    void router.navigate({
      to: '/dashboard/estimates/$id',
      params: { id },
      search: listSearch,
    });
  }

  function handleFiltersChange(patch: Partial<EstimateListFilters>) {
    void router.navigate({
      // @ts-expect-error — search required by TanStack Router v1.168
      search: (prev) => {
        const next = { ...prev, ...patch } as Record<string, unknown>;
        return cleanEstimateSearch(next);
      },
      replace: true,
    });
  }

  const handleNewClick = useCallback(() => {
    void router.navigate({ to: '/dashboard/estimates/new', search: listSearch });
  }, [router, listSearch]);

  // Reports and create/edit forms render full-page, never inside the table-view sheet.
  if (isReportsRoute || isFormMode) {
    return <Outlet />;
  }

  return (
    <WorkspaceShell hideHeader={isFormMode}>
      <EstimatesNewButtonSlot hidden={isFormMode || hasChildRoute} onClick={handleNewClick} />
      <EntityIndexLayout
        title="Estimates"
        storageKey="estimates-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() =>
          void router.navigate({ to: '/dashboard/estimates', search: listSearch })
        }
        splitWidthClass="w-[360px]"
        tableSheetWidthClass={
          isFormMode ? 'w-[95vw] sm:max-w-[1200px]' : 'w-[620px] sm:max-w-[620px]'
        }
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        // NOT-228: estimates open full-page like Jobs. Hiding the list whenever a
        // detail child route is active gives the detail the full table-area width;
        // disableSplitView removes the master-detail List view (index = Table) and
        // fullPageFormInTable renders the detail Outlet full-page instead of the
        // side sheet.
        hideListInSplit={isFormMode || hasChildRoute}
        disableSplitView
        fullPageFormInTable
        tableView={() => (
          <EstimateTableView
            initialEstimateNumber={filters.estimateNumber}
            onOpen={(id) =>
              void router.navigate({
                to: '/dashboard/estimates/$id',
                params: { id },
                search: listSearch,
              })
            }
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="flex min-h-0 flex-1 flex-col md:hidden">
            {hasChildRoute ? (
              <Outlet />
            ) : (
              <EstimateListPanel
                selectedId={selectedId}
                onSelect={handleSelect}
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            )}
          </div>
        }
      />
    </WorkspaceShell>
  );
}

function EstimatesNewButtonSlot({ hidden, onClick }: { hidden: boolean; onClick: () => void }) {
  const canViewEstimates = useHasPermission('estimates:view');
  const pricingHidden = useIsFieldHidden('PRICING');
  const newButton = useMemo(() => {
    if (hidden || pricingHidden) return null;
    return (
      <Button size="sm" onClick={onClick}>
        <Plus size={14} className="mr-1.5" />
        Add estimate
      </Button>
    );
  }, [hidden, onClick, pricingHidden]);

  const toolbarActions = useMemo(() => {
    if (hidden || !canViewEstimates || pricingHidden) return null;
    return (
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" title="Estimate reports" asChild>
          <Link to="/dashboard/estimates/reports" search={(prev) => prev as never}>
            <BarChart3 size={14} />
            Reports
          </Link>
        </Button>
      </div>
    );
  }, [hidden, canViewEstimates, pricingHidden]);

  useWorkspaceHeader({ newButton, toolbarActions }, [newButton, toolbarActions]);
  return null;
}
